/**
 * Interactive Map component with city markers for Mexico and USA
 */
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useEffect, useState } from 'react';
import 'leaflet/dist/leaflet.css';

// Fix leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom Clipper marker icon - different colors for Mexico vs USA
const createCustomIcon = (isHovered = false, country = 'México') => {
  const color = country === 'USA' ? '#3B82F6' : '#CCFF00'; // Blue for USA, lime for Mexico
  const size = isHovered ? 'w-6 h-6' : 'w-5 h-5';
  const pulseSize = 'w-10 h-10';
  
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div class="relative flex items-center justify-center">
        <div class="absolute ${pulseSize} rounded-full ${isHovered ? 'animate-ping' : 'animate-pulse'}" style="background-color: ${color}33;"></div>
        <div class="${size} rounded-full border-2 border-white shadow-lg" style="background-color: ${color}; transition: transform 0.2s ease; box-shadow: 0 0 10px ${color};"></div>
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
};

// Expanded bounds to include Mexico and Southwest USA
const MAP_BOUNDS = [
  [14.5, -125],  // Southwest (Baja California)
  [38, -86.5],   // Northeast (extended to include Texas, Arizona, California)
];

// Center point - slightly west to account for login panel covering center
const MAP_CENTER = [27, -112];  // Centered more west to show Sonora region better

// City marker component
const CityMarker = ({ city, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const country = city.country || 'México';
  
  return (
    <Marker
      position={[city.lat, city.lng]}
      icon={createCustomIcon(isHovered, country)}
      eventHandlers={{
        mouseover: () => setIsHovered(true),
        mouseout: () => setIsHovered(false),
        click: () => onClick?.(city),
      }}
    >
      <Popup className="custom-popup">
        <div className="bg-card text-foreground p-3 rounded-lg min-w-[150px]">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-display font-bold text-sm text-primary">{city.name}</h3>
            <span className="text-xs">
              {country === 'USA' ? '🇺🇸' : '🇲🇽'}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {city.clubs} {city.clubs === 1 ? 'club' : 'clubes'} · {city.courts || 0} canchas
          </p>
        </div>
      </Popup>
    </Marker>
  );
};

// Map controller for animations
const MapController = ({ selectedCity }) => {
  const map = useMap();
  
  useEffect(() => {
    if (selectedCity) {
      map.flyTo([selectedCity.lat, selectedCity.lng], 10, {
        duration: 1.5,
      });
    }
  }, [selectedCity, map]);
  
  return null;
};

export const MexicoMap = ({ cities = [], onCityClick, selectedCity }) => {
  return (
    <div className="relative w-full h-full">
      <MapContainer
        center={MAP_CENTER}
        zoom={5}
        minZoom={4}
        maxZoom={15}
        maxBounds={MAP_BOUNDS}
        className="w-full h-full"
        zoomControl={false}
        attributionControl={false}
      >
        {/* Dark map tiles */}
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        
        {/* City markers */}
        {cities.map((city, index) => (
          <CityMarker
            key={`${city.name}-${city.country || index}`}
            city={city}
            onClick={onCityClick}
          />
        ))}
        
        <MapController selectedCity={selectedCity} />
      </MapContainer>
      
      {/* Subtle vignette effect */}
      <div className="absolute inset-0 pointer-events-none z-[400]" 
        style={{
          background: 'radial-gradient(ellipse at center, transparent 40%, rgba(9,9,11,0.4) 100%)'
        }}
      />
    </div>
  );
};

// Mini map for weather widget
export const MiniMap = ({ lat, lng, className = '' }) => {
  return (
    <div className={`rounded-lg overflow-hidden ${className}`}>
      <MapContainer
        center={[lat, lng]}
        zoom={14}
        className="w-full h-full"
        zoomControl={false}
        attributionControl={false}
        dragging={false}
        scrollWheelZoom={false}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        />
        <Marker position={[lat, lng]} icon={createCustomIcon()} />
      </MapContainer>
    </div>
  );
};

export default MexicoMap;
